import React from "react";
import { useTranslation } from "react-i18next";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import { MoreVertical } from "lucide-react";
import type { Group } from "../../domain/entities/Member";
import EditIcon from "@/assets/icons/edit.svg?react";
import DeleteIcon from "@/assets/icons/delete.svg?react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  classicTableOuterClass,
  classicTableScrollClass,
  classicStickyTableHeaderClass,
} from "@/components/layout/tableLayout";

interface GroupsTableProps {
  groups: (Group & { member_count?: number; created_at?: string })[];
  isLoading?: boolean;
  onEdit: (group: Group) => void;
  onDelete: (group: Group) => void;
  onViewMembers: (group: Group) => void;
  canEdit?: boolean;
  canDelete?: boolean;
}

export const GroupsTable: React.FC<GroupsTableProps> = ({
  groups,
  isLoading,
  onEdit,
  onDelete,
  onViewMembers,
  canEdit = true,
  canDelete = true,
}) => {
  const { t } = useTranslation("membersGroups");
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };
  return (
    <div className="mt-4">
      <div className="space-y-3 md:hidden">
        {isLoading ? (
          <div className="rounded-xl border bg-white p-6 text-center text-muted-foreground">
            {t("groupsTable.loading")}
          </div>
        ) : groups.length === 0 ? (
          <div className="rounded-xl border bg-white p-6 text-center text-muted-foreground">
            {t("groupsTable.empty")}
          </div>
        ) : (
          groups.map((group) => (
            <div key={group.id} className="rounded-xl border bg-white p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <button
                    type="button"
                    onClick={() => onViewMembers(group)}
                    className="app-text-button max-w-full truncate text-left"
                  >
                    {group.name}
                  </button>
                  <p className="mt-1 text-sm text-gray-500">
                    {t("groupsTable.members", { count: group.member_count ?? 0 })}
                  </p>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button type="button" className="rounded-md p-1">
                      <MoreVertical className="h-4 w-4" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => onViewMembers(group)}>
                      {t("groupsTable.viewMembers")}
                    </DropdownMenuItem>
                    {canEdit && (
                      <DropdownMenuItem onClick={() => onEdit(group)}>
                        <EditIcon />
                        {t("groupsTable.edit")}
                      </DropdownMenuItem>
                    )}
                    {canDelete && (
                      <DropdownMenuItem
                        onClick={() => onDelete(group)}
                        className="text-destructive-text"
                      >
                        <DeleteIcon />
                        {t("groupsTable.delete")}
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              <p className="mt-4 text-sm text-gray-600">
                {group.created_at
                  ? t("groupsTable.created", { date: formatDate(group.created_at) })
                  : "-"}
              </p>
            </div>
          ))
        )}
      </div>

      <div className={classicTableOuterClass}>
        <div className={classicTableScrollClass}>
        <Table className="w-full min-w-[500px]">
          {/* Header */}
          <TableHeader className={classicStickyTableHeaderClass}>
            <TableRow className="bg-[#D8DDE6] hover:bg-[#D8DDE6]">
              <TableHead className="py-3 px-4">{t("groupsTable.nameCol")}</TableHead>
              <TableHead className="py-3 px-4">{t("groupsTable.membersCol")}</TableHead>
              <TableHead className="py-3 px-4">{t("groupsTable.createdCol")}</TableHead>
              <TableHead className="py-3 px-4 text-right">{t("groupsTable.actionsCol")}</TableHead>
            </TableRow>
          </TableHeader>

          {/* Body */}
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell
                  colSpan={4}
                  className="text-center py-8 text-muted-foreground"
                >
                  {t("groupsTable.loading")}
                </TableCell>
              </TableRow>
            ) : groups.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={4}
                  className="text-center py-8 text-muted-foreground"
                >
                  {t("groupsTable.empty")}
                </TableCell>
              </TableRow>
            ) : (
              groups.map((group) => {
                return (
                  <TableRow key={group.id}>
                    {/* Group Name */}
                    <TableCell className="py-3 px-4">
                      <button
                        type="button"
                        onClick={() => onViewMembers(group)}
                        className="app-text-button text-left"
                      >
                        {group.name}
                      </button>
                    </TableCell>

                    {/* Members Count */}
                    <TableCell className="py-3 px-4">
                      <button
                        type="button"
                        onClick={() => onViewMembers(group)}
                        className="app-text-button"
                      >
                        {group.member_count ?? 0}
                      </button>
                    </TableCell>

                    {/* Created */}
                    <TableCell className="py-3 px-4">
                      {group.created_at ? formatDate(group.created_at) : "-"}
                    </TableCell>

                    {/* Actions */}
                    <TableCell className="py-3 px-4 flex justify-end">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button>
                            <MoreVertical className="w-4 h-4 cursor-pointer" />
                          </button>
                        </DropdownMenuTrigger>

                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() => onViewMembers(group)}
                            className="cursor-pointer"
                          >
                            {t("groupsTable.viewMembers")}
                          </DropdownMenuItem>
                          {canEdit ? (
                            <DropdownMenuItem onClick={() => onEdit(group)}>
                              <EditIcon />
                              {t("groupsTable.edit")}
                            </DropdownMenuItem>
                          ) : (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div className="flex items-center gap-2 rounded-sm px-2 py-1.5 text-sm opacity-50 cursor-not-allowed select-none">
                                  <EditIcon />
                                  {t("groupsTable.edit")}
                                </div>
                              </TooltipTrigger>
                              <TooltipContent side="left">
                                {t("groupsTable.accessDenied")}
                              </TooltipContent>
                            </Tooltip>
                          )}

                          {canDelete ? (
                            <DropdownMenuItem
                              onClick={() => onDelete(group)}
                              className="text-destructive-text hover:text-destructive-text"
                            >
                              <DeleteIcon />
                              {t("groupsTable.delete")}
                            </DropdownMenuItem>
                          ) : (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div className="flex items-center gap-2 rounded-sm px-2 py-1.5 text-sm opacity-50 cursor-not-allowed select-none text-destructive-text">
                                  <DeleteIcon />
                                  {t("groupsTable.delete")}
                                </div>
                              </TooltipTrigger>
                              <TooltipContent side="left">
                                {t("groupsTable.accessDenied")}
                              </TooltipContent>
                            </Tooltip>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
        </div>
      </div>
    </div>
  );
};
